import { useState, useEffect } from "react";
import { TrendingUp } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import apiClient from "@/lib/api";

export default function PerformancePage() {
  const [trends, setTrends] = useState([]);
  const [players, setPlayers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get("/attendance/trends"),
      apiClient.get("/players"),
      apiClient.get("/sessions"),
    ])
      .then(([t, p, s]) => {
        setTrends(t.data);
        setPlayers(p.data);
        setSessions(s.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const monthlyData = Object.entries(
    sessions.reduce((acc, s) => {
      const month = s.date ? s.date.substring(0, 7) : "Unknown";
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([month, count]) => ({ month, sessions: count }))
    .slice(-6);

  const positionBreakdown = Object.entries(
    players.reduce((acc, p) => {
      acc[p.position] = (acc[p.position] || 0) + 1;
      return acc;
    }, {})
  ).map(([pos, count]) => ({ pos, count }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasData = trends.length > 0 || sessions.length > 0 || players.length > 0;

  return (
    <div className="p-6 md:p-8" data-testid="performance-page">
      <div className="mb-8">
        <h1
          className="text-3xl font-black text-gray-900"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Performance
        </h1>
        <p className="text-gray-600 mt-1">Analytics and trends for your squad</p>
      </div>

      {!hasData ? (
        <div className="text-center py-16 bg-white rounded-sm border border-gray-200">
          <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3
            className="text-lg font-bold text-gray-900 mb-2"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            No data yet
          </h3>
          <p className="text-gray-500 text-sm">
            Add players, log sessions, and track attendance to see performance trends here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {trends.length > 0 && (
            <div className="bg-white rounded-sm border border-gray-200 p-6" data-testid="attendance-chart">
              <h2
                className="text-lg font-bold text-gray-900 mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Attendance Rate per Session
              </h2>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={trends} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="session" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    domain={[0, 100]}
                    unit="%"
                  />
                  <Tooltip
                    formatter={(v, name) => [`${v}%`, "Attendance Rate"]}
                    contentStyle={{ borderRadius: 4, fontSize: 12 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#16a34a"
                    strokeWidth={2.5}
                    dot={{ fill: "#16a34a", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {monthlyData.length > 0 && (
            <div className="bg-white rounded-sm border border-gray-200 p-6" data-testid="sessions-chart">
              <h2
                className="text-lg font-bold text-gray-900 mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Sessions per Month
              </h2>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={monthlyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="sessions" fill="#16a34a" radius={[3, 3, 0, 0]} name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {positionBreakdown.length > 0 && (
            <div className="bg-white rounded-sm border border-gray-200 p-6" data-testid="squad-breakdown">
              <h2
                className="text-lg font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Squad Composition
              </h2>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
                {positionBreakdown.map(({ pos, count }) => (
                  <div key={pos} className="text-center p-4 bg-gray-50 rounded-sm border border-gray-100">
                    <div
                      className="text-2xl font-black text-gray-900 mb-1"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {count}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                      {pos}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-sm border border-gray-200 p-6">
            <h2
              className="text-lg font-bold text-gray-900 mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Summary Stats
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: "Total Players", value: players.length },
                { label: "Total Sessions", value: sessions.length },
                { label: "Sessions Tracked", value: trends.length },
                {
                  label: "Avg Attendance Rate",
                  value: trends.length
                    ? `${(trends.reduce((s, t) => s + t.rate, 0) / trends.length).toFixed(1)}%`
                    : "N/A",
                },
                {
                  label: "Best Session Rate",
                  value: trends.length
                    ? `${Math.max(...trends.map((t) => t.rate))}%`
                    : "N/A",
                },
              ].map((item) => (
                <div key={item.label} className="p-4 bg-gray-50 rounded-sm">
                  <div
                    className="text-xl font-black text-gray-900"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {item.value}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
